import { useCallback, useRef } from 'react';
import useSWR from 'swr';
import { toast } from 'sonner';
import { 
  campaignService, 
  Campaign,
  type CampaignLead as _CampaignLead,
  SequenceStep, 
  UpdateCampaignSettingsData,
} from '@/lib/services/campaigns';

const fetcher = (url: string) => fetch(url, { credentials: 'include' }).then((res) => res.json());

export function useCampaign(id: string) {
  const { data: campaign, mutate, isLoading, error } = useSWR<Campaign>(
    id ? `/api/campaigns/${id}` : null,
    fetcher,
    {
      revalidateOnFocus: false,
      revalidateOnReconnect: false,
      dedupingInterval: 0,
    }
  );

  const lastSavedName = useRef<string>('');
  const lastSavedSequence = useRef<string>('');
  const lastSavedSettings = useRef<string>('');

  const updateName = useCallback(async (name: string) => {
    if (!id || !name.trim()) return;
    if (name === lastSavedName.current) return;

    try {
      const updated = await campaignService.updateCampaignName(id, { name });
      lastSavedName.current = name;
      mutate(updated, false);
      return updated;
    } catch (error) {
      console.error('Failed to update campaign name:', error);
      toast.error('Failed to update campaign name');
      throw error;
    }
  }, [id, mutate]);

  const updateSequence = useCallback(async (sequence: SequenceStep[]) => {
    if (!id) return;
    const seqString = JSON.stringify(sequence);
    if (seqString === lastSavedSequence.current) return;

    try {
      const updated = await campaignService.updateCampaignSequence(id, { sequence });
      lastSavedSequence.current = seqString;
      mutate(updated, false);
      return updated;
    } catch (error) {
      console.error('Failed to update campaign sequence:', error);
      toast.error('Failed to update campaign sequence');
      throw error;
    }
  }, [id, mutate]);

  const updateSettings = useCallback(async (settings: UpdateCampaignSettingsData) => {
    if (!id) return;
    const settingsString = JSON.stringify(settings);
    if (settingsString === lastSavedSettings.current) return;

    try {
      const updated = await campaignService.updateCampaignSettings(id, settings);
      lastSavedSettings.current = settingsString;
      mutate(updated, false);
      return updated;
    } catch (error) {
      console.error('Failed to update campaign settings:', error);
      toast.error('Failed to update campaign settings');
      throw error;
    }
  }, [id, mutate]);

  const updateStatus = useCallback(async (status: 'draft' | 'active' | 'paused' | 'completed') => {
    if (!id) return;

    try {
      const updated = await campaignService.updateCampaignStatus(id, { status });
      await mutate(updated, { revalidate: false });
      
      if (status === 'active') {
        toast.success('Campaign launched successfully!');
      } else if (status === 'paused') {
        toast.success('Campaign paused');
      } else if (status === 'completed') {
        toast.success('Campaign completed');
      }
      
      return updated;
    } catch (error) {
      console.error('Failed to update campaign status:', error);
      toast.error('Failed to update campaign status');
      throw error;
    }
  }, [id, mutate]);

  const deleteCampaign = useCallback(async () => {
    if (!id) return;

    try {
      await campaignService.deleteCampaign(id);
      toast.success('Campaign deleted successfully');
      return true;
    } catch (error) {
      console.error('Failed to delete campaign:', error);
      toast.error('Failed to delete campaign');
      throw error;
    }
  }, [id]);

  const initializeRefs = useCallback((campaign: Campaign) => {
    lastSavedName.current = campaign.name;
    lastSavedSequence.current = JSON.stringify(campaign.sequence ?? []);
    lastSavedSettings.current = JSON.stringify({
      emailSubject: campaign.emailSubject,
      emailContent: campaign.emailContent,
      targetAudience: campaign.targetAudience,
    });
  }, []);

  const getLeads = useCallback(async () => {
    if (!id) return [];
    try {
      return await campaignService.getCampaignLeads(id);
    } catch (error) {
      console.error('Failed to fetch campaign leads:', error);
      toast.error('Failed to fetch campaign leads');
      throw error;
    }
  }, [id]);

  const addLeads = useCallback(async (emails: string[]) => {
    if (!id || !emails || emails.length === 0) return [];
    try {
      const newLeads = await campaignService.addCampaignLeads(id, emails);
      toast.success(`Added ${newLeads.length} lead${newLeads.length > 1 ? 's' : ''}`);
      return newLeads;
    } catch (error) {
      console.error('Failed to add campaign leads:', error);
      toast.error('Failed to add campaign leads');
      throw error;
    }
  }, [id]);

  const deleteLead = useCallback(async (leadId: string) => {
    if (!id || !leadId) return;
    try {
      await campaignService.deleteCampaignLead(id, leadId);
    } catch (error) {
      console.error('Failed to delete campaign lead:', error);
      throw error;
    }
  }, [id]);

  const markAsViewed = useCallback(async () => {
    if (!id) return;
    try {
      const result = await campaignService.markCampaignAsViewed(id);
      // Update the local cache with the new lastViewedAt
      if (campaign) {
        mutate({ ...campaign, lastViewedAt: result.lastViewedAt }, false);
      }
      return result;
    } catch (error) {
      // Don't show error toast - this is a background operation
      console.error('Failed to mark campaign as viewed:', error);
    }
  }, [id, campaign, mutate]);

  return {
    campaign,
    isLoading,
    error,
    mutate,
    updateName,
    updateSequence,
    updateSettings,
    updateStatus,
    deleteCampaign,
    initializeRefs,
    getLeads,
    addLeads,
    deleteLead,
    markAsViewed,
  };
}

export function useCampaigns() {
  const { data: campaigns, mutate, isLoading, error } = useSWR<Campaign[]>(
    '/api/campaigns',
    fetcher
  );

  const createCampaign = useCallback(async (data: { name: string }) => {
    try {
      const newCampaign = await campaignService.createCampaign(data);
      mutate([...(campaigns || []), newCampaign], false);
      toast.success('Campaign created successfully');
      return newCampaign;
    } catch (error) {
      console.error('Failed to create campaign:', error);
      toast.error('Failed to create campaign');
      throw error;
    }
  }, [campaigns, mutate]);

  const deleteCampaign = useCallback(async (id: string) => {
    try {
      await campaignService.deleteCampaign(id);
      mutate(campaigns?.filter(c => c.id !== parseInt(id)), false);
      toast.success('Campaign deleted successfully');
    } catch (error) {
      console.error('Failed to delete campaign:', error);
      toast.error('Failed to delete campaign');
      throw error;
    }
  }, [campaigns, mutate]);

  const toggleCampaignStatus = useCallback(async (id: string, currentStatus: string) => {
    try {
      const newStatus = currentStatus === 'active' ? 'paused' : 'active';
      const updated = await campaignService.updateCampaignStatus(id, { status: newStatus as any });
      
      // Revalidate to get fresh data from server
      await mutate(undefined, { revalidate: true });
      
      toast.success(`Campaign ${newStatus === 'active' ? 'activated' : 'paused'}`);
      return updated;
    } catch (error) {
      console.error('Failed to toggle campaign status:', error);
      toast.error('Failed to update campaign status');
      throw error;
    }
  }, [mutate]);

  return {
    campaigns,
    isLoading,
    error,
    mutate,
    createCampaign,
    deleteCampaign,
    toggleCampaignStatus,
  };
}

